var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from 'react';
import { Text, Button } from '@ui';
import { Status } from '@ui/atoms/Status';
import { translate } from '@utils/i18n';
import usePostSyncIntegrationStatus from '@hooks/requests/integration-status.post.hook';
import { resolveRowStatus } from '../types';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import './SyncStatusRowStyles.scss';
const NAMESPACE = 'features.settings.integrations.sync_status_row';
const STATUS_COLOR = {
    green: 'green-300',
    red: 'red-500',
    gray: 'gray-500'
};
/**
 * Single row in a sync status panel.
 * Displays label, status pill (with relative time), and optional sync button.
 */
export function SyncStatusRow({ label, category, syncable = false, timestampVerb = 'synced', subject }) {
    const rowStatus = resolveRowStatus(category);
    const statusColor = STATUS_COLOR[rowStatus];
    const { syncState, syncCategory, syncError } = usePostSyncIntegrationStatus();
    const [queued, setQueued] = useState(false);
    const statusLabel = buildStatusLabel(category, timestampVerb);
    const handleSyncNow = () => __awaiter(this, void 0, void 0, function* () {
        setQueued(true);
        yield syncCategory(category.category);
        // Re-enable on next poll (queued state persists briefly to prevent double-tap)
        setTimeout(() => setQueued(false), 5000);
    });
    const isSyncDisabled = queued || syncState === 'loading';
    const syncButtonLabel = queued
        ? translate('queued', { namespace: NAMESPACE })
        : translate('sync_now', { namespace: NAMESPACE });
    return (React.createElement("div", { className: "qmb-ui-sync-status-row" },
        React.createElement("div", { className: "sync-status-row__label-cell" },
            React.createElement(Text, { variant: "body", className: "sync-status-row__label" }, label)),
        React.createElement("div", { className: "sync-status-row__status-cell" },
            React.createElement(Status, { label: statusLabel, color: statusColor }),
            subject && (React.createElement(Text, { variant: "x-small", className: "sync-status-row__subject-link" },
                React.createElement("a", { href: subject.url }, translate('view_subject', { namespace: NAMESPACE, label: subject.label }))))),
        syncable && (React.createElement("div", { className: "sync-status-row__action-cell" },
            React.createElement(Button, { variant: "secondary", onClick: () => {
                    void handleSyncNow();
                }, disabled: isSyncDisabled, "aria-label": `${syncButtonLabel} — ${label}` }, syncButtonLabel))),
        syncError && (React.createElement("div", { className: "sync-status-row__inline-error", role: "alert" },
            React.createElement(Text, { variant: "x-small", className: "sync-status-row__error-text" }, syncError)))));
}
function buildStatusLabel(category, timestampVerb) {
    const NAMESPACE_ROW = 'features.settings.integrations.sync_status_row';
    if (category.lastSuccessAt) {
        return translate(timestampVerb === 'received' ? 'last_received' : 'last_synced', {
            namespace: NAMESPACE_ROW,
            time: formatRelativeTime(category.lastSuccessAt, translate('today', { namespace: NAMESPACE_ROW }))
        });
    }
    return translate(timestampVerb === 'received' ? 'never_received' : 'never_synced', { namespace: NAMESPACE_ROW });
}
